import { useMemo } from "react";

// Utility function for quarter calculations
const getQuarterDates = (date, isPrevious) => {
  const quarter = Math.floor(date.getMonth() / 3);
  const year = date.getFullYear() + (isPrevious ? -1 : 0);
  const startMonth = isPrevious ? (quarter - 1) * 3 : quarter * 3;
  return {
    start: new Date(year, startMonth, 1),
    end: new Date(year, startMonth + 3, 0),
  };
};

/**
 * Custom hook to manage date ranges based on a due date selection
 * @param {string} dueDateField - Form field name for due date
 * @param {string} dateFromField - Form field name for start date
 * @param {string} dateToField - Form field name for end date
 * @returns {{ due: any }} - Current due date value
 */
export default function useDateRange(formMethods, dueDateField, dateFromField, dateToField) {
  const { watch, setValue } = formMethods;
  const due = watch(dueDateField);

  useMemo(() => {
    const today = new Date();
    let start, end;

    if (!due?.value) {
      setValue(dateFromField, "");
      setValue(dateToField, "");
      return;
    }

    switch (due.value) {
      case "manually":
      case "today":
        start = today;
        end = today;
        break;

      case "week":
        start = new Date(today);
        start.setDate(today.getDate() - (today.getDay() || 7) + 1);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;

      case "this_week_to_date":
        start = new Date(today);
        start.setDate(today.getDate() - (today.getDay() || 7) + 1);
        end = today;
        break;

      case "month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;

      case "this_month_to_date":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;

      case "this_quarter":
        ({ start, end } = getQuarterDates(today));
        break;

      case "this_quarter_to_date":
        ({ start } = getQuarterDates(today));
        end = today;
        break;

      case "year":
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;

      case "this_year_to_date":
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;

      case "last_week":
        start = new Date(today);
        start.setDate(today.getDate() - (today.getDay() || 7) - 6);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;

      case "last_week_to_date":
        start = new Date(today);
        start.setDate(today.getDate() - (today.getDay() || 7) - 6);
        end = today;
        break;

      case "last_month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;

      case "last_month_to_date":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = today;
        break;

      case "last_quarter":
        ({ start, end } = getQuarterDates(today, true));
        break;

      case "last_quarter_to_date":
        ({ start } = getQuarterDates(today, true));
        end = today;
        break;

      case "last_year":
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;

      case "last_year_to_date":
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = today;
        break;

      case "last_30_days":
        start = new Date(today);
        start.setDate(today.getDate() - 30);
        end = today;
        break;

      case "last_365_days":
        start = new Date(today);
        start.setDate(today.getDate() - 365);
        end = today;
        break;

      case "to_date":
        start = new Date(today.getFullYear(), 0, 1);
        end = today;
        break;

      default:
        start = today;
        end = today;
        break;
    }

    setValue(dateFromField, start);
    setValue(dateToField, end);
  }, [due, setValue, dateFromField, dateToField]);

  return { due };
};
